import React, { useState } from "react";
import { Typography } from "@progress/kendo-react-common";
import ShadowCard from "../../../components/common/ShadowCard";
import { Button } from "@progress/kendo-react-buttons";
import { FaPencilAlt, FaTimes } from "react-icons/fa";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Field, FieldArray } from "@progress/kendo-react-form";
import FormTextField from "../../../components/formFields/FormTextField";
import IconComponent from "../../../components/common/IconComponent";
import "../b2bmodule.scss";

// Component for the FieldArray rows
const PackageOptionsTable = (props: any) => {
  const { value, onPush, onRemove } = props;
  const [newName, setNewName] = useState("");

  const handlePush = () => {
    if (!newName.trim()) return;

    onPush({
      value: {
        OptionName: newName,
        Hotels: [
          {
            HotelID: 0,
            MealPlanID: 0,
            RoomTypeID: 0,
            Nights: [],
            NoOfRooms: 1,
            Adults: 2,
            AWEB: 0,
            CWEB: 1,
            CNB: 0,
            Comment: "",
            NightPrices: []
          }
        ],
        HotelSpecialInclusions: [],
        MarkupType: "Amount",
        MarkupValue: 0,
        AccommodationsTotal: 0,
        TransportsTotal: 0,
        ExtraServicesTotal: 0,
        OverallTotal: 0,
        GrandTotal: 0
      }
    });
    setNewName("");
  };

  return (
    <div className="border rounded overflow-hidden mb-4 bg-white shadow-sm">
      <table className="options-table mb-0">
        <thead>
          <tr className="bg-light">
            <th style={{ width: "60px" }} className="ps-4">#</th>
            <th>Name</th>
            <th style={{ width: "60px" }}></th>
          </tr>
        </thead>
        <tbody>
          {value && value.map((item: any, index: number) => (
            <tr key={index}>
              <td className="fw-bold text-muted ps-4">{index + 1}</td>
              <td className="py-2">
                <Field
                  name={`PackageOptions[${index}].OptionName`}
                  placeholder="Enter package name"
                  component={FormTextField}
                  wrapperClassName="m-0"
                />
              </td>
              <td className="text-center pe-3">
                <Button
                  type="button"
                  fillMode="flat"
                  className="remove-row-btn"
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to reduce the options from ${value.length} to ${value.length - 1}? Data associated with removed options will be removed.`)) {
                      onRemove({ index });
                    }
                  }}
                  disabled={value.length <= 1}
                >
                  <IconComponent icon={FaTimes} />
                </Button>
              </td>
            </tr>
          ))}
          {/* Inline Add New row */}
          <tr>
            <td className="fw-bold text-muted ps-4 px-3 align-middle text-primary">+</td>
            <td className="py-2">
              <input
                type="text"
                className="form-control form-control-sm border-dashed"
                placeholder="Enter new package name..."
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handlePush()}
                style={{ fontSize: "12px" }}
              />
            </td>
            <td className="text-center pe-3">
              <Button
                type="button"
                fillMode="outline"
                themeColor="primary"
                onClick={handlePush}
                disabled={!newName.trim()}
                style={{ fontSize: "11px", height: "28px" }}
              >
                Add
              </Button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const PackageOptionsSummary = ({ options = [] }: { options: any[] }) => (
  <div className="section-subtitle mb-0" style={{ fontSize: "13px" }}>
    {options.map((opt: any, index: number) => (
      <React.Fragment key={index}>
        {index > 0 && " • "}
        <span className="fw-bold text-dark">{index + 1}:</span> {opt?.OptionName || `Option ${index + 1}`}
      </React.Fragment>
    ))}
  </div>
);

interface PackageCategorySectionProps {
  packageOptions?: any[];
}

const PackageCategorySection: React.FC<PackageCategorySectionProps> = ({
  packageOptions = []
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const toggleDialog = () => setIsDialogOpen(!isDialogOpen);

  return (
    <ShadowCard className="b2b-quotation-module b2b-card mb-4 p-0">
      <div className="p-4 d-flex gap-3 align-items-start border-bottom border-light">
        <div className="hotel-icon-wrap shadow-sm">
          <IconComponent icon={FaPencilAlt} />
        </div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2">
            <Typography.h5 className="section-title mb-0" style={{ fontSize: "16px", fontWeight: 700 }}>
              Package Types/Categories: {packageOptions.length} Options
            </Typography.h5>
            <Button
              type="button"
              fillMode="outline"
              themeColor="primary"
              className="p-1 d-flex align-items-center justify-content-center border-secondary-subtle hover:text-white"
              style={{ width: "24px", height: "24px", minWidth: "auto", borderRadius: "6px" }}
              onClick={toggleDialog}
            >
              <IconComponent icon={FaPencilAlt} style={{ fontSize: "10px" }} />
            </Button>
          </div>
          <PackageOptionsSummary options={packageOptions} />
        </div>
      </div>

      {isDialogOpen && (
        <Dialog
          title={(
            <Typography.h5 className="section-title mb-0" style={{ fontSize: "17px", paddingLeft: "5px" }}>
              Package Types/Options
            </Typography.h5>
          )}
          onClose={toggleDialog}
          width={650}
        >
          <div className="p-3 b2b-quotation-module bg-light-subtle">
            <FieldArray
              name="PackageOptions"
              component={PackageOptionsTable}
            />

            <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top bg-white px-3 pb-3">
              <Button
                type="button"
                themeColor="primary"
                onClick={toggleDialog}
                className="px-5 fw-bold"
                style={{ borderRadius: "8px" }}
              >
                Done
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </ShadowCard>
  );
};

export default PackageCategorySection;
